import { Button, Text } from "@/design-system";
import { cn } from "@/utils";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

// Folga para o cursor atravessar do botão até o painel sem fechar.
const CLOSE_DELAY_MS = 150;

export type NavMenuItem = {
  key: string;
  title: string;
  description: string;
  /** Rota interna. */
  to?: string;
  /** Link externo: abre em nova aba. */
  href?: string;
  onSelect?: () => void;
};

type NavMenuProps = {
  label: string;
  items: NavMenuItem[];
  footerText: string;
  footerCta: { label: string; to: string };
};

const itemClassName =
  "rounded-ds-lg hover:bg-ds-brand-100 focus-visible:outline-ds-focus row-span-2 grid cursor-pointer grid-rows-subgrid gap-y-2 p-4 transition-colors focus-visible:outline-2";

/**
 * Item do header desktop com painel suspenso (Integrações, Newsroom): abre no
 * hover, no clique e pelo teclado (Esc fecha e devolve o foco ao botão). No
 * celular o header usa só o link do menu-hambúrguer — hover não existe lá.
 *
 * O painel se posiciona pelo ancestral `relative` mais próximo — o `<nav>` do
 * header —, então todos abrem alinhados no início da navegação, qualquer que
 * seja o item.
 */
export const NavMenu = ({
  label,
  items,
  footerText,
  footerCta,
}: NavMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Com mouse, o hover abre o painel antes do clique; sem isso o clique logo
  // em seguida fecharia o que a pessoa acabou de ver abrir.
  const openedByHover = useRef(false);

  const open = () => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  };
  const close = () => {
    clearTimeout(closeTimer.current);
    openedByHover.current = false;
    setIsOpen(false);
  };
  const openOnHover = () => {
    if (!isOpen) openedByHover.current = true;
    open();
  };
  const toggleOnClick = () => {
    if (isOpen && openedByHover.current) {
      openedByHover.current = false;
      return;
    }
    if (isOpen) {
      close();
      return;
    }
    openedByHover.current = false;
    open();
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(close, CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      close();
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isOpen]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div
      ref={rootRef}
      onMouseEnter={openOnHover}
      onMouseLeave={scheduleClose}
      onBlur={event => {
        if (!rootRef.current?.contains(event.relatedTarget as Node)) close();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggleOnClick}
        className={cn(
          "hover:text-ds-brand-700 flex min-h-11 cursor-pointer items-center gap-1 text-base font-medium whitespace-nowrap transition-colors",
          isOpen ? "text-ds-brand-700" : "text-ds-ink-900"
        )}
      >
        {label}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "size-4 transition-transform motion-reduce:transition-none",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* pt-3 em vez de margem: a faixa transparente mantém o hover vivo
          entre o botão e o painel. */}
      <div
        id={panelId}
        hidden={!isOpen}
        className="absolute top-full left-0 z-50 w-3xl pt-3"
      >
        <div className="rounded-ds-xl border-ds-border bg-ds-surface shadow-ds-overlay flex flex-col gap-2 border p-3">
          {/* Subgrid: título e descrição de cada item ocupam as mesmas duas
              linhas da grade, então o título mais alto define a altura de
              todos e as descrições começam alinhadas. */}
          <ul className="grid grid-cols-4 gap-x-1">
            {items.map(item => {
              const handleClick = () => {
                item.onSelect?.();
                close();
              };
              const content = (
                <>
                  <span className="font-ds-display text-ds-h4 text-ds-ink-900 flex items-start gap-1">
                    {item.title}
                    {item.href ? (
                      <>
                        <ArrowUpRight
                          aria-hidden="true"
                          className="text-ds-ink-600 mt-1 size-4 shrink-0"
                        />
                        <span className="sr-only">(abre em nova aba)</span>
                      </>
                    ) : null}
                  </span>
                  <Text as="span" size="small" tone="subtle">
                    {item.description}
                  </Text>
                </>
              );

              return (
                <li
                  key={item.key}
                  className="row-span-2 grid grid-rows-subgrid"
                >
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleClick}
                      className={itemClassName}
                    >
                      {content}
                    </a>
                  ) : (
                    <Link
                      to={item.to ?? "/"}
                      onClick={handleClick}
                      className={itemClassName}
                    >
                      {content}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="border-ds-border flex items-center justify-between gap-4 border-t px-4 pt-3 pb-1">
            <Text size="small" tone="subtle">
              {footerText}
            </Text>
            <Button asChild variant="secondary" size="sm" navigates="internal">
              <Link to={footerCta.to} onClick={close}>
                {footerCta.label}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
